/**
 * AdminMetricsDashboard — real admin metrics (STORY-M1-DASH). One batched
 * `GET /admin/metrics/overview?days=…` (BE-12) feeds 10 tiles: active-users
 * trend, signup/login funnel, chat usage (volume + p95 + intent-mix + error
 * rate), marketplace funnel, OOC demand by region/category, feedback
 * inbox/rating/SLA, seller-onboarding funnel, language split, notification
 * read-rate, outbox health. NO GMV. Mounted behind
 * RequirePermissionRoute(`ops.metrics.read`).
 *
 * Composes the P0 kit (KpiStatCard / TrendChart / DistributionChart /
 * SplitChart / AdminStates) into a responsive Tailwind grid, honouring the
 * kit's REAL contracts: `KpiStatCard` takes lowercase `testid` and no `hint`
 * (this surface has no prior-period delta, so `delta` is omitted); `AdminStates`
 * is a boolean-driven WRAPPER around the tile grid; every chart receives
 * `loadingLabel`/`emptyLabel`/`errorLabel`. All copy is an `admin` i18n key;
 * colour/spacing via --g-* tokens (no hardcoded values, no undefined `--g-text`).
 */
import { useState, type ReactElement, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import {
  AdminStates,
  DistributionChart,
  KpiStatCard,
  SplitChart,
  TrendChart,
} from "@/features/admin/_shared";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { useMetricsOverview } from "@/features/admin/dashboard/hooks";
import {
  METRICS_RANGES,
  type AuthFunnel,
  type MarketplaceFunnel,
  type MetricsOverview,
  type MetricsRange,
  type OutboxQueueHealth,
  type SellerOnboarding,
} from "@/features/admin/dashboard/api";

/** Numbers, localised (NOT money — plain integer counts, no grouping). */
function useNum(): (n: number) => string {
  const { i18n } = useTranslation(ADMIN_NS);
  return (n: number) =>
    new Intl.NumberFormat(i18n.language, {
      maximumFractionDigits: 0,
      useGrouping: false,
    }).format(n);
}

function pct(n: number): string {
  return `${Math.round(n * 100)}%`;
}

function sumBy<T>(rows: T[], pick: (r: T) => number): number {
  return rows.reduce((sum, r) => sum + pick(r), 0);
}

function authFunnelTotal(f: AuthFunnel): number {
  return f.signup_started + f.signup_completed + f.login_success + f.login_failure;
}

function marketplaceFunnelTotal(f: MarketplaceFunnel): number {
  return f.searches + f.product_views + f.seller_views + f.contacts + f.directions;
}

function sellerFunnelTotal(s: SellerOnboarding): number {
  return (
    s.submitted +
    s.email_verified +
    s.phone_verified +
    s.photo_uploaded +
    s.approved +
    s.rejected +
    s.dropped_off
  );
}

/** True when every tile is empty/zero — drives the kit empty state. */
function isOverviewEmpty(d: MetricsOverview): boolean {
  return (
    d.active_users.mau === 0 &&
    d.chat_usage.messages_total === 0 &&
    authFunnelTotal(d.auth_funnel) === 0 &&
    marketplaceFunnelTotal(d.marketplace_funnel) === 0 &&
    sellerFunnelTotal(d.seller_onboarding) === 0 &&
    d.ooc_demand.total === 0 &&
    d.feedback.open_count === 0 &&
    d.feedback.replied_count === 0 &&
    d.feedback.closed_count === 0 &&
    d.notifications.created === 0 &&
    sumBy(d.outbox_health.rows, (r: OutboxQueueHealth) => r.pending + r.failed) === 0 &&
    d.language_split.slices.length === 0
  );
}

/** A titled tile shell — layout only (--g-* tokens). */
function Tile({
  title,
  children,
  span,
  testid,
}: {
  title: string;
  children: ReactNode;
  span?: boolean;
  testid?: string;
}): ReactElement {
  return (
    <section
      data-testid={testid}
      className={`rounded-[var(--g-radius-md)] border border-[var(--g-hairline)] bg-[var(--g-surface)] p-4 ${
        span ? "md:col-span-2" : ""
      }`}
    >
      <h2 className="mb-3 text-sm font-medium text-muted-foreground">{title}</h2>
      {children}
    </section>
  );
}

export function AdminMetricsDashboard(): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const num = useNum();
  const [range, setRange] = useState<MetricsRange>("30d");
  const { data, isLoading, isError, isFetching, refetch } = useMetricsOverview(range);

  // Every chart REQUIRES these three label strings (kit contract).
  const chartStateLabels = {
    loadingLabel: t("dashboard.states.loading"),
    emptyLabel: t("dashboard.chart.noData"),
    errorLabel: t("dashboard.states.error"),
  };

  let body: ReactElement | null = null;
  if (data !== undefined) {
    const c = data.chat_usage;
    const f = data.feedback;
    const af = data.auth_funnel;
    const mf = data.marketplace_funnel;
    const so = data.seller_onboarding;
    const outboxPending = sumBy(data.outbox_health.rows, (r) => r.pending);
    const outboxFailed = sumBy(data.outbox_health.rows, (r) => r.failed);

    // Flat-int funnel DTOs → DistributionChart {label,value}[].
    const authFunnelData = [
      { label: t("dashboard.stage.signup_started"), value: af.signup_started },
      { label: t("dashboard.stage.signup_completed"), value: af.signup_completed },
      { label: t("dashboard.stage.login_success"), value: af.login_success },
      { label: t("dashboard.stage.login_failure"), value: af.login_failure },
    ];
    const marketplaceFunnelData = [
      { label: t("dashboard.stage.search"), value: mf.searches },
      { label: t("dashboard.stage.product_view"), value: mf.product_views },
      { label: t("dashboard.stage.seller_view"), value: mf.seller_views },
      { label: t("dashboard.stage.contact"), value: mf.contacts },
      { label: t("dashboard.stage.directions"), value: mf.directions },
    ];
    const sellerFunnelData = [
      { label: t("dashboard.stage.submitted"), value: so.submitted },
      { label: t("dashboard.stage.email_verified"), value: so.email_verified },
      { label: t("dashboard.stage.phone_verified"), value: so.phone_verified },
      { label: t("dashboard.stage.photo_uploaded"), value: so.photo_uploaded },
      { label: t("dashboard.stage.approved"), value: so.approved },
      { label: t("dashboard.stage.rejected"), value: so.rejected },
      { label: t("dashboard.stage.dropped_off"), value: so.dropped_off },
    ];

    body = (
      <div data-testid="admin-dashboard" className="mt-4">
        {/* KPI strip — no `hint`, no `delta` (kit contract). */}
        <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <KpiStatCard
            testid="kpi-active-users"
            label={t("dashboard.kpi.activeUsers")}
            value={num(data.active_users.mau)}
          />
          <KpiStatCard
            testid="kpi-chat-latency"
            label={t("dashboard.kpi.chatLatencyP95")}
            value={
              c.latency_p95_ms === null
                ? t("dashboard.na")
                : `${num(c.latency_p95_ms)} ${t("dashboard.unit.ms")}`
            }
          />
          <KpiStatCard
            testid="kpi-chat-volume"
            label={t("dashboard.kpi.chatMessages")}
            value={num(c.messages_total)}
          />
          <KpiStatCard
            testid="kpi-notification-read-rate"
            label={t("dashboard.kpi.notificationReadRate")}
            value={pct(data.notifications.read_rate)}
          />
          <KpiStatCard
            testid="kpi-feedback-rating"
            label={t("dashboard.kpi.feedbackRating")}
            value={f.avg_rating === null ? t("dashboard.na") : f.avg_rating.toFixed(1)}
          />
          <KpiStatCard
            testid="kpi-feedback-sla"
            label={t("dashboard.kpi.feedbackSlaBreaches")}
            value={num(f.sla_breaches)}
          />
          <KpiStatCard
            testid="kpi-outbox-pending"
            label={t("dashboard.kpi.outboxPending")}
            value={num(outboxPending)}
          />
          <KpiStatCard
            testid="kpi-ooc-total"
            label={t("dashboard.kpi.oocDemand")}
            value={num(data.ooc_demand.total)}
          />
        </div>

        {/* Chart grid */}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Tile title={t("dashboard.tile.activeUsers")} testid="tile-active-users" span>
            <TrendChart
              ariaLabel={t("dashboard.tile.activeUsers")}
              data={data.active_users.dau.map((p) => ({ x: p.date, y: p.count }))}
              {...chartStateLabels}
            />
          </Tile>

          <Tile title={t("dashboard.tile.chatVolume")} testid="tile-chat-volume">
            <TrendChart
              ariaLabel={t("dashboard.tile.chatVolume")}
              data={c.daily_volume.map((p) => ({ x: p.date, y: p.count }))}
              {...chartStateLabels}
            />
          </Tile>

          <Tile title={t("dashboard.tile.intentMix")} testid="tile-intent-mix">
            <DistributionChart
              ariaLabel={t("dashboard.tile.intentMix")}
              data={c.intent_mix.map((s) => ({
                label: t(`dashboard.intent.${s.intent}`, s.intent),
                value: s.count,
              }))}
              {...chartStateLabels}
            />
          </Tile>

          <Tile title={t("dashboard.tile.signupFunnel")} testid="tile-signup-funnel">
            <DistributionChart
              ariaLabel={t("dashboard.tile.signupFunnel")}
              data={authFunnelData}
              {...chartStateLabels}
            />
          </Tile>

          <Tile title={t("dashboard.tile.marketplaceFunnel")} testid="tile-marketplace-funnel">
            <DistributionChart
              ariaLabel={t("dashboard.tile.marketplaceFunnel")}
              data={marketplaceFunnelData}
              {...chartStateLabels}
            />
          </Tile>

          <Tile title={t("dashboard.tile.sellerFunnel")} testid="tile-seller-funnel">
            <DistributionChart
              ariaLabel={t("dashboard.tile.sellerFunnel")}
              data={sellerFunnelData}
              {...chartStateLabels}
            />
          </Tile>

          <Tile title={t("dashboard.tile.oocRegion")} testid="tile-ooc-region">
            <DistributionChart
              ariaLabel={t("dashboard.tile.oocRegion")}
              data={data.ooc_demand.by_region.map((s) => ({
                label: t(`dashboard.region.${s.key}`, s.key),
                value: s.count,
              }))}
              {...chartStateLabels}
            />
          </Tile>

          <Tile title={t("dashboard.tile.oocCategory")} testid="tile-ooc-category">
            <DistributionChart
              ariaLabel={t("dashboard.tile.oocCategory")}
              data={data.ooc_demand.by_category.map((s) => ({
                label: t(`dashboard.category.${s.key}`, s.key),
                value: s.count,
              }))}
              {...chartStateLabels}
            />
          </Tile>

          <Tile title={t("dashboard.tile.languageSplit")} testid="tile-language-split">
            <SplitChart
              ariaLabel={t("dashboard.tile.languageSplit")}
              data={data.language_split.slices.map((s) => ({
                label: t(`dashboard.lang.${s.key}`, s.key),
                value: s.count,
              }))}
              {...chartStateLabels}
            />
          </Tile>
        </div>

        <p className="mt-3 text-xs text-muted-foreground">
          {t("dashboard.kpi.outboxFailed", { count: outboxFailed })}
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-lg font-semibold text-[var(--g-primary-ink)]">
          {t("dashboard.title")}
        </h1>
        {/* flex-wrap: on narrow viewports (~<400px) the range group + reload
            button no longer fit alongside the title on one row. Without
            wrapping, the row overflowed the (overflow-hidden) admin shell
            and the reload button was pushed off-screen — reachable only via
            an undiscoverable horizontal scroll, i.e. "not clickable" from a
            user's perspective (SCRUM-149). Wrapping keeps every control
            visible and reachable at any viewport width. */}
        <div className="flex flex-wrap items-center gap-2">
          <div role="group" aria-label={t("dashboard.rangeLabel")} className="flex flex-wrap gap-1">
            {METRICS_RANGES.map((r) => (
              <button
                key={r}
                type="button"
                data-testid={`range-${r}`}
                aria-pressed={r === range}
                onClick={() => setRange(r)}
                className={`min-h-[44px] rounded-[var(--g-radius-sm)] px-3 text-sm ${
                  r === range
                    ? "bg-[var(--g-primary)] text-[var(--g-on-primary)]"
                    : "border border-[var(--g-hairline)] text-foreground"
                }`}
              >
                {t(`dashboard.range.${r}`)}
              </button>
            ))}
          </div>
          {/* Plain reload — lives OUTSIDE AdminStates (no kit `onRetry`). The
              `.g-spin` ring (same primitive <Button>'s `loading` prop uses)
              gives visible click feedback — without it a fast local fetch
              finishes before any human perceives a change, and the button
              reads as unresponsive (SCRUM-149). */}
          <button
            type="button"
            data-testid="dashboard-reload"
            onClick={() => void refetch()}
            disabled={isFetching}
            aria-busy={isFetching}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] px-3 text-sm text-foreground disabled:opacity-50"
          >
            {isFetching && (
              <span
                data-testid="dashboard-reload-spinner"
                aria-hidden="true"
                className="g-spin inline-block h-[13px] w-[13px] rounded-full border-2 border-current border-t-transparent"
              />
            )}
            {t("dashboard.reload")}
          </button>
        </div>
      </div>

      <AdminStates
        isLoading={isLoading}
        isError={isError}
        isEmpty={data !== undefined && isOverviewEmpty(data)}
        loadingLabel={t("dashboard.states.loading")}
        emptyLabel={t("dashboard.states.empty")}
        errorLabel={t("dashboard.states.error")}
        testid="admin-dashboard-states"
      >
        {body}
      </AdminStates>
    </div>
  );
}

export default AdminMetricsDashboard;
